/* ==========================================================================
   Lawn Tips - dark mode toggle (OPT-IN ONLY)

   Builds the sun/moon button and drops it into the header icon row next to
   the cart. Deliberately has NO prefers-color-scheme fallback: the site
   stays light for everyone until a visitor clicks the button. Their choice
   is remembered on that device only.

   The pre-paint half of this lives inline in snippets/meta-tags.liquid,
   because it has to run before first paint or returning dark-mode visitors
   get a white flash. This file only handles the button and the flip.

   Styles: assets/lt-dark.css
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'lt-theme';
  var META_DARK = '#262624';
  var META_LIGHT = '#ffffff';

  var MOON =
    '<svg class="lt-icon-moon" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
    ' stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"' +
    ' focusable="false"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/></svg>';

  var SUN =
    '<svg class="lt-icon-sun" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
    ' stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"' +
    ' focusable="false"><circle cx="12" cy="12" r="4.2"/>' +
    '<path d="M12 2.5v2M12 19.5v2M4.6 4.6 6 6M18 18l1.4 1.4M2.5 12h2M19.5 12h2' +
    'M4.6 19.4 6 18M18 6l1.4-1.4"/></svg>';

  function isDark() {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  }

  function store(value) {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      /* private browsing / storage disabled: the toggle still works for this page */
    }
  }

  /* Keeps the mobile browser chrome in step with the page */
  function syncMetaColour(dark) {
    var meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', dark ? META_DARK : META_LIGHT);
  }

  function apply(dark) {
    if (dark) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    syncMetaColour(dark);
  }

  function syncButton(btn) {
    var dark = isDark();
    var label = dark ? 'Switch to light mode' : 'Switch to dark mode';
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
    btn.setAttribute('aria-pressed', dark ? 'true' : 'false');
    var text = btn.querySelector('.lt-tt-label');
    if (text) text.textContent = dark ? 'Light mode' : 'Dark mode';
  }

  function syncAllToggles() {
    document.querySelectorAll('#lt-theme-toggle, #lt-theme-toggle-drawer').forEach(syncButton);
  }

  function build() {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'lt-theme-toggle';
    btn.className = 'lt-theme-toggle';
    btn.innerHTML = MOON + SUN;
    syncButton(btn);

    btn.addEventListener('click', function () {
      var next = !isDark();
      store(next ? 'dark' : 'light');
      var run = function () {
        apply(next);
        syncAllToggles();
      };
      /* Cross-fade the flip where the browser supports it, otherwise just swap */
      if (document.startViewTransition) {
        try {
          document.startViewTransition(run);
        } catch (e) {
          run();
        }
      } else {
        run();
      }
    });

    return btn;
  }

  function place() {
    if (document.getElementById('lt-theme-toggle')) return;

    var icons = document.querySelector('.header__icons');
    if (!icons) return; /* app pages hide the header entirely: no toggle, by design */

    var btn = build();

    /* Sit to the left of the cart. Walk up to whichever node is the direct
       child of .header__icons, since Dawn wraps the cart link. */
    var ref = icons.querySelector('#cart-icon-bubble') || icons.querySelector('a[href*="/cart"]');
    while (ref && ref.parentNode !== icons) {
      ref = ref.parentNode;
    }

    if (ref) {
      icons.insertBefore(btn, ref);
    } else {
      icons.appendChild(btn);
    }
  }

  syncMetaColour(isDark());

  /* -----------------------------------------------------------------------
     Not dark-mode: a rendering bug fix that runs in BOTH modes.

     The Section Store sliders (ss-slider-2) hardcode loading="lazy" on
     their slide images, and Swiper clones every slide 4x and positions
     them with transforms. Chrome's native lazy-loader often never fires
     for transformed carousel slides, so the Blog / Lawn Help / Grubs tiles
     rendered as empty boxes. This has been the case on the LIVE theme
     since the 28 Jul image optimisation; it is just easier to see against
     a dark page.

     Fix: flip those images to eager at LOW fetch priority (so they never
     compete with the hero/LCP), and re-kick any that already gave up.
     ----------------------------------------------------------------------- */
  function fixCarouselLazyImages() {
    document
      .querySelectorAll('.swiper-slide img[loading="lazy"], [class*="slider-image-"] img[loading="lazy"]')
      .forEach(function (img) {
        img.loading = 'eager';
        if (!img.hasAttribute('fetchpriority')) img.setAttribute('fetchpriority', 'low');
        if (!img.complete || img.naturalWidth === 0) {
          var s = img.getAttribute('src');
          if (s) {
            img.removeAttribute('src');
            img.setAttribute('src', s);
          }
        }
      });
  }

  /* Safety net for the mobile scroll lock.

     Dawn locks page scroll while the menu drawer is open by putting
     overflow-hidden-<breakpoint> on the body, and removes it in its own
     close() method. If anything strips the drawer's open attribute without
     going through close(), that class is orphaned and the visitor cannot
     scroll the page until they reload.

     Only the menu drawer's suffixed classes are cleared here. The cart
     drawer and search modal use plain 'overflow-hidden', so they are
     untouched and keep working normally. */
  function releaseStuckScrollLock() {
    var drawer = document.querySelector('.menu-drawer-container');
    if (drawer && drawer.hasAttribute('open')) return;
    ['overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop'].forEach(
      function (c) {
        document.body.classList.remove(c);
      }
    );
  }

  /* Mobile: the header hides the icon toggle (CSS), so the menu drawer gets
     a labelled row next to Log in. Same behaviour, natural mobile home, and
     the centred logo stays centred.

     This control is deliberately a div with role="button", NOT a <button>.
     Dawn's menu-drawer registers a close handler on EVERY button inside the
     drawer (they are all menu-drawer__close-button by design). A <button>
     here was therefore treated as a close button: Dawn removed the drawer's
     open attribute but left the body scroll-lock class behind, so mobile
     visitors who switched theme landed on a page they could not scroll until
     they refreshed. A div is invisible to that selector, so the drawer keeps
     its own state and releases the lock properly when the user closes it. */
  function placeDrawerToggle() {
    if (document.getElementById('lt-theme-toggle-drawer')) return;
    var util = document.querySelector('.menu-drawer__utility-links');
    if (!util) return;

    var btn = document.createElement('div');
    btn.id = 'lt-theme-toggle-drawer';
    btn.className = 'lt-theme-toggle-drawer';
    btn.setAttribute('role', 'button');
    btn.setAttribute('tabindex', '0');
    btn.innerHTML = MOON + SUN + '<span class="lt-tt-label"></span>';
    syncButton(btn);

    var flip = function (event) {
      if (event) event.preventDefault();
      var next = !isDark();
      store(next ? 'dark' : 'light');
      apply(next);
      syncAllToggles();
      /* belt and braces: outlast Dawn's 400ms close animation in case any
         other script closes the drawer off the back of this tap */
      releaseStuckScrollLock();
      setTimeout(releaseStuckScrollLock, 600);
    };

    btn.addEventListener('click', flip);
    btn.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') flip(event);
    });

    util.insertBefore(btn, util.firstChild);
  }

  /* Preview-only: Shopify's preview-bar iframe (#PBarNextFrame) sometimes
     sits at full viewport size and swallows the first click on every page
     (verified: clicks produce no page events and can even trigger stray
     navigation). While it covers most of the screen, disable its pointer
     events; hand them back once Shopify collapses it to the actual bar.
     The iframe only exists on theme previews, so this never runs on the
     published site. */
  function tamePreviewBar() {
    var bar = document.getElementById('PBarNextFrame');
    if (!bar) return;
    var r = bar.getBoundingClientRect();
    var huge = r.width >= innerWidth * 0.9 && r.height >= innerHeight * 0.9;
    bar.style.pointerEvents = huge ? 'none' : '';
  }

  function boot() {
    place();
    placeDrawerToggle();
    tamePreviewBar();
    setInterval(tamePreviewBar, 1000);
    fixCarouselLazyImages();
    /* Swiper clones slides at init, after DOMContentLoaded: sweep again */
    setTimeout(fixCarouselLazyImages, 1500);
    setTimeout(fixCarouselLazyImages, 4000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The header is a section, so re-place the buttons after a theme-editor reload */
  document.addEventListener('shopify:section:load', function () {
    place();
    placeDrawerToggle();
  });
})();
